import React, { useState } from "react";
import { Table, DatePicker, Select, Button, Popover } from "antd";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import { BASE_API } from "../Config/Config";

const { Option } = Select;

function Reports() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { dropdownData } = location.state || {};

    const [region, setRegion] = useState("");
    const [warehouseDD, setWarehouseDD] = useState([]);
    const [warehouse, setWarehouse] = useState("");
    const [date, setDate] = useState(null);
    const [availablePercent, setAvailablePercent] = useState("");
    const [tableData, setTableData] = useState([]);
    const [loading, setLoading] = useState(false);

    const availableOptions = [10, 20, 50, 70, 90, 100];

    const fetchwarehouseDD = async (values) => {
        try {
            const formData = new FormData();
            formData.append("region_id", values);

            const response = await fetch(
                `${BASE_API}/apis/GetWarehouseDD.php`,
                {
                    method: "POST",
                    credentials: "include",
                    body: formData,
                }
            );

            const result = await response.json();

            if (result.status === "success") {
                setWarehouseDD(result?.data);
            } else {
                setWarehouseDD([]);
            }
        } catch (err) {
            console.error("Fetch Error:", err);
            setWarehouseDD([]);
        }
    }

    const columns = [
        { title: t("sno"), dataIndex: "key", key: "key", align: "center" },
        { title: t("region"), dataIndex: "region", key: "region", align: "center" },
        { title: t("warehouse"), dataIndex: "warehouse", key: "warehouse", align: "center" },
        { title: t("date"), dataIndex: "date", key: "date", align: "center" },
        { title: t("totalCapacity"), dataIndex: "totalCapacity", key: "totalCapacity", align: "center" },
        { title: t("occupied"), dataIndex: "occupied", key: "occupied", align: "center" },
        { title: t("available"), dataIndex: "available", key: "available", align: "center" },
        { title: t("occupiedPercent"), dataIndex: "occupiedPercent", key: "occupiedPercent", align: "center" },
        { title: t("availablePercent"), dataIndex: "availablePercent", key: "availablePercent", align: "center" },
    ];

    const handleSubmit = async () => {
        if (!date) {
            Swal.fire({
                icon: "warning",
                title: t("requiredFields"),
                text: t("selectDateAlert"),
            });
            return;
        }

        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("wh_id", warehouse || "");
            formData.append("region", region || "");
            formData.append("date", date.format("YYYY-MM-DD"));
            formData.append("available_percentage", availablePercent || "");

            const response = await fetch(`${BASE_API}/apis/QAGetReport.php`, {
                method: "POST",
                body: formData,
                credentials: "include",
            });

            const result = await response.json();

            if (result.status === "success" && Array.isArray(result.data)) {
                const mappedData = result.data.map((item, index) => ({
                    key: index + 1,
                    region: item?.region,
                    warehouse: item?.wh_name,
                    date: item?.date,
                    totalCapacity: item?.capacity_summary[0]?.total_capacity,
                    available: item?.capacity_summary[0]?.available,
                    occupied: item?.capacity_summary[0]?.occupancy,
                    availablePercent: item?.capacity_summary[0]?.available_percentage,
                    occupiedPercent: item?.capacity_summary[0]?.occupancy_percentage,
                }));

                if (result.total) {
                    mappedData.push({
                        key: "total",
                        region: "",
                        warehouse: t("total"),
                        date: "",
                        totalCapacity: result.total.total_capacity,
                        occupied: result.total.total_occupancy,
                        availablePercent: result.total.total_available_percentage,
                        isTotal: true,
                    });
                }

                setTableData(mappedData);
            } else {
                setTableData([]);
                Swal.fire({
                    icon: "info",
                    title: t("info"),
                    text: t("noDataFound"),
                });
            }
        } catch (err) {
            console.error(err);
            setTableData([]);
            Swal.fire({
                icon: "error",
                title: t("error"),
                text: t("somethingWentWrong"),
            });
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (type) => {
        if (!date) {
            Swal.fire({
                icon: "warning",
                title: t("requiredFields"),
                text: t("selectDateAlert"),
            });
            return;
        }

        try {
            const queryParams = new URLSearchParams({
                wh_id: warehouse || "",
                region: region || "",
                date: date.format("YYYY-MM-DD"),
                available_percentage: availablePercent || "",
                type,
            });

            const apiUrl = `${BASE_API}/apis/QAReportDownload.php?${queryParams.toString()}`;
            const response = await fetch(apiUrl, {
                method: "GET",
                credentials: "include"
            });
            if (!response.ok) throw new Error(t("downloadFailed"));

            const blob = await response.blob();
            const contentType = response.headers.get("content-type");
            let fileExtension = "csv";
            if (contentType.includes("pdf")) fileExtension = "pdf";
            else if (contentType.includes("excel") || contentType.includes("spreadsheet")) fileExtension = "xlsx";

            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `Warehouse_Report_${date.format("YYYY-MM-DD")}.${fileExtension}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);

            Swal.fire({
                icon: "success",
                title: t("downloadStarted"),
                text: t("reportDownloading"),
            });
        } catch (err) {
            console.error(err);
            Swal.fire({
                icon: "error",
                title: t("downloadFailed"),
                text: err.message || t("somethingWentWrong"),
            });
        }
    };

    return (
        <div className="container mt-4">

            {/* Top bar */}
            <div className="d-flex justify-content-between align-items-center mb-4">
                <h3 className="text-primary">{t("warehouseReport")}</h3>
                <Button type="default" onClick={() => navigate(-1)}>
                    <i className="bi bi-arrow-left"></i> {t("back")}
                </Button>
            </div>

            {/* Filters */}
            <div className="row g-3 mb-4">
                <div className="col-md-2">
                    <label className="fw-semibold">
                        <i className="bi bi-calendar-week me-2 text-secondary"></i>
                        {t("date")} <span className="text-danger">*</span>
                    </label>
                    <DatePicker
                        value={date}
                        format="DD-MM-YYYY"
                        onChange={setDate}
                        className="w-100"
                        placeholder={t("selectRegion")}
                        disabledDate={(current) => current && current > dayjs().endOf("day")}
                    />
                </div>

                <div className="col-md-2">
                    <label className="fw-semibold">
                        <i className="bi bi-geo-alt me-2 text-secondary"></i>
                        {t("region")}
                    </label>
                    <Select
                        allowClear
                        placeholder={t("selectRegion")}
                        value={region || undefined}
                        onChange={(value) => {
                            setRegion(value);
                            fetchwarehouseDD(value);
                        }}
                        className="w-100"
                    >
                        {dropdownData?.map((r, i) => (
                            <Option key={i} value={r?.region_id}>{r?.region_name}</Option>
                        ))}
                    </Select>
                </div>

                <div className="col-md-2">
                    <label className="fw-semibold">
                        <i className="bi bi-building me-2 text-secondary"></i>
                        {t("warehouse")}
                    </label>
                    <Select
                        allowClear
                        placeholder={t("selectWarehouse")}
                        value={warehouse || undefined}
                        onChange={setWarehouse}
                        className="w-100"
                    >
                        {warehouseDD?.map((w, i) => (
                            <Option key={i} value={w?.Wh_idh}>{w?.wh_name}</Option>
                        ))}
                    </Select>
                </div>

                <div className="col-md-2">
                    <label className="fw-semibold">
                        <i className="bi bi-percent me-2 text-secondary"></i>
                        {t("availablePercentLabel")}
                    </label>
                    <Select
                        allowClear
                        placeholder={t("selectPercent")}
                        value={availablePercent || undefined}
                        onChange={setAvailablePercent}
                        className="w-100"
                    >
                        <Option value="all">{t("all")}</Option>
                        {availableOptions.map((p) => (
                            <Option key={p} value={p}>{`>${p}%`}</Option>
                        ))}
                    </Select>
                </div>

                <div className="col-md-2 d-flex align-items-end">
                    <Button type="primary" onClick={handleSubmit}>{t("submit")}</Button>
                </div>
            </div>

            {/* Download */}
            {tableData.length > 0 && (
                <div className="col d-flex justify-content-end align-items-end mb-4">
                    <Popover
                        content={
                            <div className="d-flex flex-column">
                                <Button className="btn-sm mb-1" onClick={() => handleDownload("csv")}>CSV</Button>
                                <Button className="btn-sm" onClick={() => handleDownload("pdf")}>PDF</Button>
                            </div>
                        }
                        placement="left"
                        trigger="click"
                    >
                        <Button type="primary">{t("reportDownload")}</Button>
                    </Popover>
                </div>
            )}

            {/* Table */}
            <div className="card shadow-sm border-0">
                <div className="card-body">
                    <Table
                        className="custom-ant-table"
                        columns={columns}
                        dataSource={tableData}
                        bordered
                        loading={loading}
                        scroll={{ x: "max-content" }}
                        pagination={{
                            showSizeChanger: true,
                            pageSizeOptions: ["10", "20", "50"],
                            position: ["bottomRight"]
                        }}
                        rowClassName={(record) =>
                            record.occupied > record.totalCapacity ? "row-danger" : ""
                        }
                        locale={{ emptyText: t("noData") }}
                    />
                </div>
            </div>
        </div>
    );
}

export default Reports;

// import React, { useState, useEffect } from "react";
// import { Table, DatePicker, Select, Button, message, Popover } from "antd";
// import { useLocation, useNavigate } from "react-router-dom";
// import dayjs from "dayjs";
// import Swal from "sweetalert2";
// import { BASE_API } from "../Config/Config";

// function Reports() {

//     const navigate = useNavigate();
//     const location = useLocation();

//     const { Option } = Select;
//     const { dropdownData } = location.state || {};

//     // Filters state
//     const [region, setRegion] = useState("");
//     const [warehouse, setWarehouse] = useState("");
//     const [date, setDate] = useState(null);
//     const [availablePercent, setAvailablePercent] = useState("");

//     // Table data
//     const [tableData, setTableData] = useState([]);
//     const [loading, setLoading] = useState(false);

//     // Example options for filters (replace with API if needed)
//     const availableOptions = [10, 20, 50, 70, 90, 100];

//     // Table columns
//     const columns = [
//         {
//             title: "S.No",
//             dataIndex: "key",
//             key: "key",
//             align: "center",
//         },
//         {
//             title: "Region",
//             dataIndex: "region",
//             key: "region",
//             align: "center",
//         },
//         {
//             title: "Warehouse",
//             dataIndex: "warehouse",
//             key: "warehouse",
//             align: "center",
//         },
//         {
//             title: "Date",
//             dataIndex: "date",
//             key: "date",
//             align: "center",
//         },
//         {
//             title: "Total Capacity",
//             dataIndex: "totalCapacity",
//             key: "totalCapacity",
//             align: "center",
//         },
//         {
//             title: "Occupied",
//             dataIndex: "occupied",
//             key: "occupied",
//             align: "center",
//         },
//         {
//             title: "Available",
//             dataIndex: "available",
//             key: "available",
//             align: "center",
//         },
//         {
//             title: "Occupied (%)",
//             dataIndex: "occupiedPercent",
//             key: "occupiedPercent",
//             align: "center",
//         },
//         {
//             title: "Available (%)",
//             dataIndex: "availablePercent",
//             key: "availablePercent",
//             align: "center",
//         },
//     ];

//     const handleSubmit = async () => {
//         try {
//             setLoading(true);

//             // Validate required filters
//             if (!date) {
//                 Swal.fire({
//                     icon: "warning",
//                     title: "Required Fields",
//                     text: "You must select Date ",
//                 });
//                 return;
//             }

//             const formData = new FormData();
//             formData.append("wh_id", warehouse || "");
//             formData.append("region", region || "");
//             formData.append("date", date ? date.format("YYYY-MM-DD") : "");
//             formData.append("available_percentage", availablePercent || "");

//             const response = await fetch(
//                 `${BASE_API}/apis/WarehouseReport.php`,
//                 {
//                     method: "POST",
//                     body: formData,
//                 }
//             );

//             const result = await response.json();

//             if (result.status === "success" && Array.isArray(result.data)) {
//                 // Map API data to table structure
//                 const mappedData = result.data.map((item, index) => ({
//                     key: index + 1,
//                     region: item?.region,
//                     warehouse: item?.wh_name,
//                     date: item?.date,
//                     totalCapacity: item?.capacity_summary[0]?.total_capacity,
//                     available: item?.capacity_summary[0]?.available,
//                     occupied: item?.capacity_summary[0]?.occupancy,
//                     availablePercent: item?.capacity_summary[0]?.available_percentage,
//                     occupiedPercent: item?.capacity_summary[0]?.occupancy_percentage,
//                 }));

//                 // Append total row if API provides totals
//                 if (result.total) {
//                     mappedData.push({
//                         key: "total",
//                         region: "",
//                         warehouse: "Total",
//                         date: "",
//                         totalCapacity: result.total.total_capacity,
//                         occupied: result.total.total_occupancy,
//                         availablePercent: result.total.total_available_percentage,
//                         isTotal: true,
//                     });
//                 }
//                 setTableData(mappedData);

//             } else {
//                 setTableData([]);
//                 Swal.fire({
//                     icon: "info",
//                     title: "Info",
//                     text: "No data found for selected filters",
//                 });
//             }
//         } catch (err) {
//             console.error("Fetch Error:", err);
//             setTableData([]);
//             Swal.fire({
//                 icon: "error",
//                 title: "Error",
//                 text: "Something went Wrong"
//             });

//         } finally {
//             setLoading(false);
//         }
//     };

//     const handleDownload = async (type) => {
//         try {
//             // Validate filters before download
//             if (!date) {
//                 Swal.fire({
//                     icon: "warning",
//                     title: "Required Fields",
//                     text: "Please select the Date before downloading.",
//                 });
//                 return;
//             }

//             // Build query string
//             const queryParams = new URLSearchParams({
//                 wh_id: warehouse || "",
//                 region: region,
//                 date: date.format("YYYY-MM-DD"),
//                 available_percentage: availablePercent || "",
//                 type: type,
//             });

//             const apiUrl = `${BASE_API}/apis/WarehouseDown.php?${queryParams.toString()}`;

//             const response = await fetch(apiUrl, {
//                 method: "GET",
//             });

//             if (!response.ok) {
//                 throw new Error("Failed to fetch report");
//             }

//             const blob = await response.blob();

//             const contentType = response.headers.get("content-type");

//             let fileExtension = "csv";

//             if (contentType.includes("pdf")) {
//                 fileExtension = "pdf";
//             } else if (contentType.includes("spreadsheet") || contentType.includes("excel")) {
//                 fileExtension = "xlsx";
//             }

//             const url = window.URL.createObjectURL(blob);
//             const a = document.createElement("a");
//             a.href = url;

//             a.download = `Warehouse_Report_${date.format("YYYY-MM-DD")}.${fileExtension}`;

//             document.body.appendChild(a);
//             a.click();
//             a.remove();
//             window.URL.revokeObjectURL(url);

//             Swal.fire({
//                 icon: "success",
//                 title: "Download Started",
//                 text: "Your warehouse report is downloading.",
//             });

//         } catch (err) {
//             console.error(err);
//             Swal.fire({
//                 icon: "error",
//                 title: "Download Failed",
//                 text: err.message || "Something went wrong while downloading the report.",
//             });
//         }
//     };

//     const WarehouseDD = dropdownData?.Warehouse?.filter((item) => (item?.region === region))

//     return (
//         <div className="container mt-4">

//             {/* Top bar */}
//             <div className="d-flex justify-content-between align-items-center mb-4">
//                 <h3 className="text-primary">Warehouse Report</h3>
//                 <Button
//                     type="default"
//                     onClick={() => navigate(-1)} // go back
//                 >
//                     <i className="bi bi-arrow-left"></i>
//                     Back
//                 </Button>
//             </div>

//             {/* Filters */}
//             <div className="row g-3 mb-4">
//                 <div className="col-md-2">
//                     <label className="fw-semibold">
//                         <i class="bi bi-calendar-week me-2 text-secondary"></i>
//                         Date <span className="text-danger">*</span></label>
//                     <DatePicker
//                         value={date}
//                         format="DD-MM-YYYY"
//                         onChange={setDate}
//                         className="w-100"
//                         disabledDate={(current) =>
//                             current && current > dayjs().endOf("day")
//                         }
//                     />
//                 </div>

//                 <div className="col-md-2">
//                     <label className="fw-semibold">
//                         <i className="bi bi-geo-alt me-2 text-secondary"></i>
//                         Region </label>
//                     <Select
//                         allowClear
//                         placeholder="Select Region"
//                         value={region || undefined}
//                         onChange={setRegion}
//                         className="w-100"
//                     >
//                         {dropdownData.Region.map((r, i) => (
//                             <Option key={i} value={r}>{r}</Option>
//                         ))}
//                     </Select>
//                 </div>

//                 <div className="col-md-2">
//                     <label className="fw-semibold">
//                         <i className="bi bi-building me-2 text-secondary"></i>
//                         Warehouse</label>
//                     <Select
//                         allowClear
//                         placeholder="Select Warehouse"
//                         value={warehouse || undefined}
//                         onChange={setWarehouse}
//                         className="w-100"
//                     >
//                         {WarehouseDD.map((w, i) => (
//                             <Option key={i} value={w?.id}>{w?.name}</Option>
//                         ))}
//                     </Select>
//                 </div>

//                 <div className="col-md-2">
//                     <label className="fw-semibold">
//                         <i class="bi bi-percent me-2 text-secondary"></i>
//                         Available ≥ %</label>
//                     <Select
//                         allowClear
//                         placeholder="Select %"
//                         value={availablePercent || undefined}
//                         onChange={setAvailablePercent}
//                         className="w-100"
//                     >
//                         <Option value="all">All</Option>
//                         {availableOptions.map((p) => (
//                             <Option key={p} value={p}>{`>${p}%`}</Option>
//                         ))}
//                     </Select>
//                 </div>
//                 <div className="col-md-2 d-flex align-items-end">
//                     <Button type="primary" onClick={handleSubmit}>
//                         Submit
//                     </Button>
//                 </div>
//             </div>


//             {tableData.length > 0 && (
//                 <div className="col d-flex justify-content-end align-items-end mb-4">
//                     <button className="btn btn-primary">
//                         <Popover
//                             content={
//                                 <div>
//                                     <Button className="btn btn-sm" onClick={() => handleDownload("csv")}>CSV</Button><br />
//                                     <Button className="btn btn-sm" onClick={() => handleDownload("pdf")}>PDF</Button>
//                                 </div>
//                             }
//                             placement="left"
//                             trigger="click"
//                             className="p-4 me-3"
//                         >
//                             Report Download
//                         </Popover>
//                     </button>
//                 </div>
//             )}


//             {/* Table */}
//             <div className="card shadow-sm border-0">
//                 <div className="card-body">
//                     <style>
//                         {`
//                             .custom-ant-table .ant-table-thead > tr > th {
//                             background-color: #536aa0;
//                             color: white;
//                             font-weight: 600;
//                             text-align: center;
//                             }

//                             .custom-ant-table .ant-table {
//                             border-radius: 10px;
//                             overflow: hidden;
//                             }

//                             .custom-ant-table .ant-table-tbody > tr:hover > td {
//                             background: #f5f7ff;
//                             }

//                             /* 🔴 Over capacity row */
//                             .row-danger td {
//                             background-color: #ffe3e5 !important;
//                             }
//                         `}
//                     </style>
//                     <Table
//                         className="custom-ant-table"
//                         columns={columns}
//                         dataSource={tableData}
//                         bordered
//                         loading={loading}
//                         scroll={{ x: "max-content" }} // horizontal scroll on mobile
//                         pagination={{
//                             showSizeChanger: true,
//                             pageSizeOptions: ["10", "20", "50"],
//                             position: ["bottomRight"]
//                         }}
//                         rowClassName={(record) =>
//                             record.occupancy > record.total_capacity ? "row-danger" : ""
//                         }
//                     />
//                 </div>
//             </div>

//         </div>
//     );
// };

// export default Reports;


